<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
              <path d="M4 22h16"></path>
              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
            </svg>
          </div>
          <h5 class="modal-title">{{ isEdit ? 'Edit' : 'New' }} Training Program</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body">

      <!-- Program Title -->
      <div class="setting-item">
        <label for="programTitle">Program Title <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </div>
          <input
            id="programTitle"
            v-model="form.title"
            type="text"
            class="form-control"
            :class="{ 'border-danger': v$.title.$error }"
            placeholder="e.g. Annual Security Training"
            autofocus
            @blur="v$.title.$touch()"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.title.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">Program title is required.</div>
        </div>
      </div>

      <div class="settings-grid">
        <!-- Assignment Frequency -->
        <div class="setting-item mb-2">
          <label for="frequencySelect">Assignment Frequency</label>
          <div class="input-with-icon">
            <div class="input-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <select id="frequencySelect" class="form-control" v-model="form.assignment_frequency">
              <option value="On Demand">On Demand</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Annually">Annually</option>
            </select>
          </div>

          <div class="explainer-box" v-html="dynamicExplainer"></div>
        </div>

        <!-- Training Topics / Modules Selection -->
        <div class="setting-item mb-2">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <label class="mb-0">
              <i class="fas fa-layer-group text-theme me-1"></i> Select Training Topics
            </label>
            <span class="badge bg-light text-secondary border" style="font-size: 0.75rem;">
              {{ form.selected_module_ids.length }} selected
            </span>
          </div>
          <small class="text-muted d-block mb-2" style="font-size: 0.75rem;">
            Choose specific training modules or topics to include in this program.
          </small>

          <div class="embedded-selection-box">
            <div class="search-area p-2 border-bottom">
              <div class="input-with-icon">
                <div class="input-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </div>
                <input
                  v-model="moduleSearchQuery"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Search training topics..."
                />
              </div>
            </div>

            <div class="selection-list-container">
              <div v-if="loadingModules" class="text-center p-3">
                <div class="spinner-border spinner-border-sm text-info" role="status"></div>
                <span class="text-muted ms-2 small">Loading topics...</span>
              </div>
              <div v-else-if="filteredModules.length === 0" class="text-center text-muted p-3 small">
                No training topics found.
              </div>
              <template v-else>
                <div class="selection-item select-all-row pointer" @click="toggleSelectAllModules">
                  <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center gap-2">
                    <input
                      class="form-check-input m-0"
                      type="checkbox"
                      :checked="isAllFilteredModulesSelected"
                      @click.stop="toggleSelectAllModules"
                    >
                    <span class="fw-bold text-dark small">Select All {{ moduleSearchQuery ? 'Filtered' : '' }} Topics</span>
                  </div>
                </div>

                <div
                  v-for="mod in filteredModules"
                  :key="mod.id"
                  class="selection-item"
                  @click="toggleModuleSelection(mod.id)"
                >
                  <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center justify-content-between pointer w-100">
                    <div class="d-flex align-items-center gap-2 me-2 overflow-hidden flex-grow-1">
                      <input class="form-check-input m-0 flex-shrink-0" type="checkbox" :value="mod.id" v-model="form.selected_module_ids" @click.stop>
                      <span class="text-dark small fw-semibold text-truncate d-inline-block">{{ mod.name }}</span>
                    </div>
                    <span class="badge flex-shrink-0" :class="mod.type === 'video' ? 'bg-secondary' : 'bg-theme'">
                      <i :class="mod.type === 'video' ? 'fas fa-play-circle' : 'fas fa-laptop-code'" class="me-1"></i>
                      {{ mod.type === 'video' ? 'Video' : 'SCORM' }}
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Toggles: Reminders, Mandatory, Assign All Users -->
        <div class="setting-item mt-2">
          <!-- Training Reminders -->
          <div class="d-flex justify-content-between align-items-center bg-light-surface p-3 rounded border mb-2">
            <div>
              <label class="mb-0 text-dark fw-bold"><i class="fas fa-bell text-theme me-2"></i> Training Reminders</label>
              <small class="d-block text-muted mt-1" style="font-size: 0.75rem;">
                Send automated email reminders to users with incomplete trainings.
              </small>
            </div>
            <label class="pure-toggle mb-0" data-testid="toggle-program-reminder">
              <input type="checkbox" v-model="form.enable_reminders">
              <span class="pure-slider"></span>
            </label>
          </div>

          <!-- Mandatory Training -->
          <div class="d-flex justify-content-between align-items-center bg-light-surface p-3 rounded border mb-2">
            <div>
              <label class="mb-0 text-dark fw-bold"><i class="fas fa-clipboard-check text-theme me-2"></i> Mandatory Training?</label>
              <small class="d-block text-muted mt-1" style="font-size: 0.75rem;">
                If disabled, this program will not count towards dashboard compliance metrics and overdue reports.
              </small>
            </div>
            <label class="pure-toggle mb-0" data-testid="toggle-program-mandatory">
              <input type="checkbox" v-model="form.is_mandatory">
              <span class="pure-slider"></span>
            </label>
          </div>

          <!-- Assign to All Users Toggle -->
          <div class="d-flex justify-content-between align-items-center bg-light-surface p-3 rounded border">
            <div>
              <label class="mb-0 text-dark fw-bold"><i class="fas fa-users text-theme me-2"></i> Assign to All Users</label>
              <small class="d-block text-muted mt-1" style="font-size: 0.75rem;">
                {{ form.assign_to_all ? 'Automatically assign this program to all active users.' : 'Select specific users to assign this program to.' }}
              </small>
            </div>
            <label class="pure-toggle mb-0" data-testid="toggle-program-assign-all">
              <input type="checkbox" v-model="form.assign_to_all" @change="onAssignAllToggleChange">
              <span class="pure-slider"></span>
            </label>
          </div>

          <!-- Specific Users Selection Box (shown when Assign to All Users is OFF) -->
          <div v-if="!form.assign_to_all" class="mt-2">
            <div class="embedded-selection-box" :class="{ 'border-danger': userValidationError }">
              <div class="search-area p-2 border-bottom d-flex align-items-center justify-content-between">
                <div class="input-with-icon flex-grow-1 me-2">
                  <div class="input-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </div>
                  <input
                    v-model="userSearchQuery"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Search users by name or email..."
                  />
                </div>
                <span class="badge bg-info text-white" style="font-size: 0.75rem;">
                  {{ form.selected_user_ids.length }} selected
                </span>
              </div>

              <div class="selection-list-container">
                <div v-if="loadingUsers" class="text-center p-3">
                  <div class="spinner-border spinner-border-sm text-info" role="status"></div>
                  <span class="text-muted ms-2 small">Loading users...</span>
                </div>
                <div v-else-if="filteredUsers.length === 0" class="text-center text-muted p-3 small">
                  No users found.
                </div>
                <template v-else>
                  <div class="selection-item select-all-row pointer" @click="toggleSelectAllUsers">
                    <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center gap-2">
                      <input
                        class="form-check-input m-0"
                        type="checkbox"
                        :checked="isAllFilteredUsersSelected"
                        @click.stop="toggleSelectAllUsers"
                      >
                      <span class="fw-bold text-dark small">Select All {{ userSearchQuery ? 'Filtered' : '' }} Users</span>
                    </div>
                  </div>

                  <div
                    v-for="user in filteredUsers"
                    :key="user.id"
                    class="selection-item"
                    @click="toggleUserSelection(user.id)"
                  >
                  <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center gap-2 pointer w-100">
                    <input class="form-check-input m-0 flex-shrink-0" type="checkbox" :value="user.id" v-model="form.selected_user_ids" @click.stop>
                    <div class="user-avatar-xs flex-shrink-0">
                      {{ user.first_name ? user.first_name.charAt(0) : '' }}{{ user.last_name ? user.last_name.charAt(0) : '' }}
                    </div>
                    <div class="d-flex flex-column overflow-hidden flex-grow-1">
                      <span class="fw-bold text-dark small text-truncate">{{ user.first_name }} {{ user.last_name }}</span>
                      <span class="text-muted text-truncate" style="font-size: 0.7rem;">{{ user.email || 'No email' }}</span>
                    </div>
                  </div>
                  </div>
                </template>
              </div>
            </div>
            <div v-if="userValidationError" class="text-danger mt-1 ms-1" style="font-size: 0.75rem; font-weight: 600;">
              {{ userValidationError }}
            </div>
          </div>
        </div>
      </div>

      <!-- Program Status -->
      <div
        class="status-card mt-3"
        :class="{
          'is-active': form.status === 'published',
          'is-locked': form.status === 'locked'
        }"
      >
        <div class="status-info">
          <div class="status-header">
            <span class="status-title">Program Status</span>
            <span v-if="form.status === 'published'" class="status-badge active">Published</span>
            <span v-else-if="form.status === 'locked'" class="status-badge locked">Locked</span>
            <span v-else class="status-badge inactive">Draft</span>
          </div>
          <p class="status-description">
            <template v-if="form.status === 'published'">
              This program is active and visible for assignment.
            </template>
            <template v-else-if="form.status === 'locked'">
              This program is locked. No new users can be assigned.
            </template>
            <template v-else>
              This program is currently hidden and cannot be assigned yet.
            </template>
          </p>
        </div>

        <div class="status-control">
          <select class="form-control status-dropdown" v-model="form.status">
            <option value="draft" :disabled="isEdit && initialData?.status !== 'draft'">
              Draft
            </option>
            <option value="published">Published</option>
            <option value="locked">Locked</option>
          </select>
        </div>
      </div>

    </div>

    <div class="modal-footer" :class="{'bg-warning-soft': confirmState}">
      <template v-if="!confirmState">
        <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
        <button type="button" class="btn btn-primary" @click="handleSaveClick">Save Program</button>
      </template>
      <template v-else>
        <div class="me-auto d-flex align-items-center text-dark small fw-bold">
          <i class="fas fa-exclamation-circle text-warning me-2 fs-5"></i>
          {{ confirmMessage }}
        </div>
        <button type="button" class="btn btn-secondary bg-white" @click="confirmState = false">No, Cancel</button>
        <button
          type="button"
          class="btn btn-warning fw-bold text-dark border-0 shadow-sm"
          @click="executeSave"
        >
          Yes, Proceed
        </button>
      </template>
    </div>
  </vue-final-modal>
</template>

<script setup>
import { ref, reactive, computed, watch, getCurrentInstance, defineEmits, defineProps } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

const props = defineProps(['modelValue', 'isEdit', 'initialData', 'categoryId', 'orgId']);
const emit = defineEmits(['update:modelValue', 'save']);

const { proxy } = getCurrentInstance();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const form = reactive({
  title: '',
  assignment_frequency: 'On Demand',
  status: 'draft',
  category_id: null,
  enable_reminders: true,
  is_mandatory: true,
  assign_to_all: true,
  selected_user_ids: [],
  selected_module_ids: []
});

const rules = computed(() => ({
  title: { required }
}));

const v$ = useVuelidate(rules, form);

// Topic / Module Selection State
const libraryModules = ref([]);
const moduleSearchQuery = ref('');
const loadingModules = ref(false);

// User Selection State
const users = ref([]);
const userSearchQuery = ref('');
const loadingUsers = ref(false);
const userValidationError = ref('');

const filteredModules = computed(() => {
  const query = moduleSearchQuery.value.toLowerCase().trim();
  if (!query) return libraryModules.value;
  return libraryModules.value.filter(m => (m.name || '').toLowerCase().includes(query));
});

const isAllFilteredModulesSelected = computed(() => {
  return filteredModules.value.length > 0 &&
    filteredModules.value.every(m => form.selected_module_ids.includes(m.id));
});

const toggleSelectAllModules = () => {
  const filteredIds = filteredModules.value.map(m => m.id);
  if (isAllFilteredModulesSelected.value) {
    form.selected_module_ids = form.selected_module_ids.filter(id => !filteredIds.includes(id));
  } else {
    const newSelections = filteredIds.filter(id => !form.selected_module_ids.includes(id));
    form.selected_module_ids.push(...newSelections);
  }
};

const toggleModuleSelection = (id) => {
  const idx = form.selected_module_ids.indexOf(id);
  if (idx === -1) {
    form.selected_module_ids.push(id);
  } else {
    form.selected_module_ids.splice(idx, 1);
  }
};

const filteredUsers = computed(() => {
  const query = userSearchQuery.value.toLowerCase().trim();
  if (!query) return users.value;
  return users.value.filter(u => {
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });
});

const isAllFilteredUsersSelected = computed(() => {
  return filteredUsers.value.length > 0 &&
    filteredUsers.value.every(u => form.selected_user_ids.includes(u.id));
});

const toggleSelectAllUsers = () => {
  const filteredIds = filteredUsers.value.map(u => u.id);
  if (isAllFilteredUsersSelected.value) {
    form.selected_user_ids = form.selected_user_ids.filter(id => !filteredIds.includes(id));
  } else {
    const newSelections = filteredIds.filter(id => !form.selected_user_ids.includes(id));
    form.selected_user_ids.push(...newSelections);
  }
  userValidationError.value = '';
};

const toggleUserSelection = (id) => {
  const idx = form.selected_user_ids.indexOf(id);
  if (idx === -1) {
    form.selected_user_ids.push(id);
  } else {
    form.selected_user_ids.splice(idx, 1);
  }
  userValidationError.value = '';
};

const onAssignAllToggleChange = () => {
  userValidationError.value = '';
};

const dynamicExplainer = computed(() => {
  const freq = form.assignment_frequency;
  const count = form.selected_module_ids.length || props.initialData?.modules?.length || 0;

  if (freq === 'On Demand') {
    return "This program has no automated schedule. Users will only receive modules when <strong>manually assigned</strong> or through ad-hoc requests.";
  }

  let totalDays = 0;
  let exampleText = "";

  if (freq === 'Monthly') {
    totalDays = 30;
    exampleText = "For example, 4 training modules in a monthly program would mean one module is assigned approx. every <strong>7 days</strong>.";
  } else if (freq === 'Quarterly') {
    totalDays = 90;
    exampleText = "For example, 6 training modules in a quarterly program would mean one module is assigned approx. every <strong>15 days</strong>.";
  } else if (freq === 'Annually') {
    totalDays = 365;
    exampleText = "For example, 24 training modules in an annual program would mean one module is assigned approx. every <strong>2 weeks</strong>.";
  }

  // If it's a brand new program or has 0 modules, show the helpful example
  if (count === 0) {
    return `Training video/module assignment frequency is determined by how many training items are in the Program. ${exampleText}`;
  }

  // If there are modules, calculate the exact real-world interval!
  const intervalDays = Math.max(1, Math.round(totalDays / count));
  const pluralItem = count > 1 ? 'items' : 'item';
  const pluralDay = intervalDays > 1 ? 'days' : 'day';

  return `This program currently contains <strong>${count} training ${pluralItem}</strong>. Based on a ${freq} schedule (${totalDays} days), the system will automatically assign one module to users approximately every <strong>${intervalDays} ${pluralDay}</strong>.`;
});

const fetchLibraryModules = async () => {
  if (!props.orgId) return;
  loadingModules.value = true;
  try {
    const res = await proxy.$axios.get(`/orgadmin/trainings/organisations/${props.orgId}/all-modules`);
    if (res.data.success) {
      const raw = res.data.modules || [];
      libraryModules.value = raw.map(m => ({
        id: m.id || m.ID,
        name: m.name || m.Name,
        type: (m.type || m.Type || '').toLowerCase(),
        scorm_version: m.scorm_version || m.ScormVersion,
        is_active: m.is_active !== undefined ? m.is_active : true,
        display_path: m.display_path || m.launch_path || m.LaunchPath || ''
      })).filter(m => m.is_active);
    }
  } catch (e) {
    console.error("Failed to load modules for ProgramModal", e);
  } finally {
    loadingModules.value = false;
  }
};

const fetchOrganisationUsers = async () => {
  if (!props.orgId) return;
  loadingUsers.value = true;
  try {
    const res = await proxy.$axios.get(`/users/organisations/${props.orgId}`);
    if (res.data.success) {
      users.value = res.data.users || [];
    }

    // Preload enrolled users if editing an individual assignment program
    if (props.isEdit && props.initialData?.id && props.initialData?.assignment_type === 'individual') {
      const enrollRes = await proxy.$axios.get(`/orgadmin/trainings/organisations/${props.orgId}/programs/${props.initialData.id}/enrollments`);
      if (enrollRes.data.success && enrollRes.data.enrollments) {
        const activeIds = users.value.map(u => u.id);
        const uniqueIds = [...new Set(enrollRes.data.enrollments.map(e => e.user_id || e.UserID))]
          .filter(id => activeIds.includes(id));
        form.selected_user_ids = uniqueIds;
      }
    }
  } catch (e) {
    console.error("Failed to load users for ProgramModal", e);
  } finally {
    loadingUsers.value = false;
  }
};

// Confirmation Banner State
const confirmState = ref(false);
const confirmMessage = ref('');

watch(() => props.modelValue, async (newVal) => {
  if (newVal) {
    moduleSearchQuery.value = '';
    userSearchQuery.value = '';
    userValidationError.value = '';
    confirmState.value = false;
    confirmMessage.value = '';

    form.title = props.initialData?.title || '';
    form.assignment_frequency = props.initialData?.assignment_frequency || 'On Demand';
    form.status = props.initialData?.status || 'draft';
    form.category_id = props.categoryId;

    form.enable_reminders = props.initialData?.enable_reminders !== false;
    form.is_mandatory = props.initialData?.is_mandatory !== false;
    form.assign_to_all = props.initialData?.assignment_type !== 'individual';

    if (props.initialData?.modules && Array.isArray(props.initialData.modules)) {
      form.selected_module_ids = props.initialData.modules.map(m => m.id || m.ID);
    } else {
      form.selected_module_ids = [];
    }
    form.selected_user_ids = [];

    v$.value.$reset();

    await Promise.all([
      fetchLibraryModules(),
      fetchOrganisationUsers()
    ]);
  }
});

const closeModal = () => {
  isOpen.value = false;
  confirmState.value = false;
};

const handleSaveClick = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  if (!form.assign_to_all && form.selected_user_ids.length === 0) {
    userValidationError.value = 'Please select at least one user when "Assign to All Users" is turned off.';
    return;
  }

  const initialModuleIds = (props.initialData?.modules || []).map(m => m.id || m.ID);
  const currentModuleIds = form.selected_module_ids || [];
  const newModuleIds = currentModuleIds.filter(id => !initialModuleIds.includes(id));
  const hasNewModules = newModuleIds.length > 0;

  const wasAssignToAll = props.isEdit && (props.initialData?.assignment_type === 'all' || props.initialData?.assignment_type === undefined);
  const isAssignToAll = form.assign_to_all;
  const isPublished = form.status === 'published';

  // Scenario 3: When New training has been added and assigned to ALL
  const isNewPublishedProgramWithModules = !props.isEdit && isPublished && isAssignToAll && currentModuleIds.length > 0;
  const isExistingProgramWithNewModules = props.isEdit && isPublished && isAssignToAll && hasNewModules;
  const isSwitchedToAllWithModules = props.isEdit && isPublished && !wasAssignToAll && isAssignToAll && currentModuleIds.length > 0;

  if (isNewPublishedProgramWithModules || isExistingProgramWithNewModules || isSwitchedToAllWithModules) {
    confirmMessage.value = "Deploying to ALL users in the organisation. Continue?";
    confirmState.value = true;
    return;
  }

  // Scenario 1 & 2: Save silently without prompt
  executeSave();
};

const executeSave = () => {
  emit('save', {
    ...form,
    assignment_type: form.assign_to_all ? 'all' : 'individual',
    selected_module_ids: [...form.selected_module_ids],
    selected_user_ids: [...form.selected_user_ids]
  });
  confirmState.value = false;
};
</script>

<style scoped>
/* Explainer Callout Box */
.explainer-box {
  margin-top: 1rem;
  padding: 0.85rem 1.25rem;
  background-color: #f8fafc;
  border-left: 4px solid #00a0c1; /* Brand color accent */
  border-radius: 0 6px 6px 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}

.explainer-box strong {
  color: #334155;
  font-weight: 600;
}

/* Modal Overlay & Container */
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.modern-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
  max-height: 90vh;
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 160, 193, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

/* Body */
.modal-body {
  padding: 1.25rem 1.5rem;
  flex-grow: 1;
  overflow-y: auto;
  max-height: calc(90vh - 130px);
  overflow-x: hidden;
}

.setting-item {
  margin-bottom: 1.25rem;
}

.setting-item label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: #64748b;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.form-control {
  width: 100%;
  padding: 0.7rem 0.7rem 0.7rem 2.5rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
  appearance: auto;
}

.form-control-sm {
  padding: 0.45rem 0.6rem 0.45rem 2rem;
  font-size: 0.85rem;
}

.form-control:focus {
  border-color: #00a0c1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

/* Embedded Selection Boxes for Topics and Users */
.embedded-selection-box {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
}

.embedded-selection-box:focus-within {
  border-color: #00a0c1;
}

.search-area {
  background-color: #f8fafc;
}

.selection-list-container {
  overflow-y: auto;
  max-height: 180px;
}

.selection-item {
  padding: 0.6rem 0.9rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.15s;
}

.selection-item:hover {
  background-color: #f8fafc;
}

.selection-item:last-child {
  border-bottom: none;
}

.select-all-row {
  background-color: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
}

.pointer {
  cursor: pointer;
}

/* Custom Checkbox & Small Avatar */
.custom-checkbox .form-check-input {
  width: 1.15rem;
  height: 1.15rem;
  border: 1.5px solid #cbd5e1;
  cursor: pointer;
}

.custom-checkbox .form-check-input:checked {
  background-color: #00a0c1;
  border-color: #00a0c1;
}

.user-avatar-xs {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* Status Card */
.status-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.status-card.is-active {
  background-color: #f0f9ff;
  border-color: #bae6fd;
}

.status-info {
  flex: 1;
  padding-right: 1rem;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.status-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1e293b;
}

.status-description {
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
}

.status-badge {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.025em;
}

.status-badge.active {
  background-color: #dcfce7;
  color: #15803d;
}

.status-badge.inactive {
  background-color: #f1f5f9;
  color: #64748b;
}

.status-badge.locked {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-card.is-locked {
  background-color: #fef2f2;
  border-color: #fecaca;
}

.status-dropdown {
  width: auto;
  min-width: 120px;
  padding: 0.4rem 2rem 0.4rem 1rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: #475569;
  cursor: pointer;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
  background-color: #f8fafc;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.btn-primary {
  background-color: #00a0c1;
  color: white;
  border-color: #00a0c1;
}

.btn-primary:hover {
  background-color: #008da8;
  border-color: #008da8;
}

.btn-secondary {
  background-color: white;
  color: #64748b;
  border-color: #e2e8f0;
}

.btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

/* Custom Pure CSS Toggle Switch */
.pure-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}
.pure-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}
.pure-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: .3s;
  border-radius: 24px;
}
.pure-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #94a3b8;
  transition: .3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
.pure-toggle input:checked + .pure-slider {
  background-color: #B5DAC6;
}
.pure-toggle input:checked + .pure-slider:before {
  transform: translateX(20px);
  background-color: #00B972;
}

.modal-footer {
  transition: background-color 0.3s ease;
}
.bg-warning-soft {
  background-color: #fffbeb !important;
  border-top-color: #fde68a !important;
}
</style>